import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { CalendarIcon, Filter, X, ChevronDown } from "lucide-react";
import { useState } from "react";
import { DateRange } from "react-day-picker";

export interface ProductReportFilters {
  searchTerm: string;
  dateRange: DateRange | undefined;
  productIds: string[];
  transactionType: "buy" | "sell" | "both";
}

interface ProductReportFiltersProps {
  filters: ProductReportFilters;
  onFiltersChange: (filters: ProductReportFilters) => void;
  products?: Array<{ id: string; name: string }>;
  isLoading?: boolean;
}

export function ProductReportFilters({
  filters,
  onFiltersChange,
  products = [],
  isLoading = false,
}: ProductReportFiltersProps) {
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const updateFilter = <K extends keyof ProductReportFilters>(
    key: K,
    value: ProductReportFilters[K],
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    onFiltersChange({
      searchTerm: "",
      dateRange: undefined,
      productIds: [],
      transactionType: "both",
    });
  };

  const hasActiveFilters =
    filters.searchTerm ||
    filters.dateRange ||
    filters.productIds.length > 0 ||
    filters.transactionType !== "both";

  const activeFilterCount = [
    filters.searchTerm,
    filters.dateRange,
    filters.productIds.length > 0 ? filters.productIds : null,
    filters.transactionType !== "both" ? filters.transactionType : null,
  ].filter(Boolean).length;

  return (
    <Card className="flex-2/3 gap-1">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Product Filters
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground ml-2">
                {activeFilterCount}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className={cn("space-y-4", "lg:block")}>
        {/* Date Range */}

        <div className="flex gap-4 flex-wrap">
          {/* Product */}
          <div className="space-y-2">
            <Label>Products</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn(
                    "w-full justify-between",
                    !filters.productIds.length && "text-muted-foreground"
                  )}
                  disabled={isLoading}
                >
                  {filters.productIds.length === 0
                    ? "Select products..."
                    : filters.productIds.length === 1
                    ? products.find((p) => p.id === filters.productIds[0])?.name
                    : `${filters.productIds.length} products selected`}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <div className="max-h-60 overflow-auto">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center space-x-2 p-2 hover:bg-accent cursor-pointer"
                      onClick={() => {
                        const isSelected = filters.productIds.includes(product.id);
                        const newProductIds = isSelected
                          ? filters.productIds.filter((id) => id !== product.id)
                          : [...filters.productIds, product.id];
                        updateFilter("productIds", newProductIds);
                      }}
                    >
                      <Checkbox
                        checked={filters.productIds.includes(product.id)}
                      />
                      <span className="text-sm">{product.name}</span>
                    </div>
                  ))}
                </div>
                {filters.productIds.length > 0 && (
                  <>
                    <Separator />
                    <div className="p-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full"
                        onClick={() => updateFilter("productIds", [])}
                      >
                        Clear all
                      </Button>
                    </div>
                  </>
                )}
              </PopoverContent>
            </Popover>
          </div>

          {/* Transaction Type */}
          <div className="space-y-2">
            <Label>Transaction Type</Label>
            <Select
              value={filters.transactionType}
              onValueChange={(value) =>
                updateFilter(
                  "transactionType",
                  value as "buy" | "sell" | "both",
                )
              }
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">All Transactions</SelectItem>
                <SelectItem value="buy">Purchases</SelectItem>
                <SelectItem value="sell">Sales</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 flex-1">
            <Label>Date Range</Label>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !filters.dateRange && "text-muted-foreground",
                  )}
                  disabled={isLoading}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.dateRange?.from ? (
                    filters.dateRange.to ? (
                      <>
                        {format(filters.dateRange.from, "LLL dd, y")} -{" "}
                        {format(filters.dateRange.to, "LLL dd, y")}
                      </>
                    ) : (
                      format(filters.dateRange.from, "LLL dd, y")
                    )
                  ) : (
                    "Pick a date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  disabled={(date) => date > new Date()}
                  initialFocus
                  mode="range"
                  defaultMonth={filters.dateRange?.from}
                  selected={filters.dateRange}
                  onSelect={(range) => {
                    updateFilter("dateRange", range);
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <>
            <Separator />
            <div className="space-y-2">
              <Label className="text-sm font-medium">Active Filters</Label>
              <div className="flex flex-wrap gap-2">
                {filters.searchTerm && (
                  <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground">
                    Search: {filters.searchTerm}
                    <X
                      className="h-4 w-4 cursor-pointer hover:text-destructive transition-colors"
                      onClick={() => updateFilter("searchTerm", "")}
                    />
                  </span>
                )}
                {filters.dateRange && (
                  <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground">
                    Date:{" "}
                    {filters.dateRange.from &&
                      format(filters.dateRange.from, "MMM dd")}{" "}
                    -{" "}
                    {filters.dateRange.to &&
                      format(filters.dateRange.to, "MMM dd")}
                    <X
                      className="h-4 w-4 cursor-pointer hover:text-destructive transition-colors"
                      onClick={() => updateFilter("dateRange", undefined)}
                    />
                  </span>
                )}
                {filters.productIds.length > 0 && (
                  <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground">
                    Products:{" "}
                    {filters.productIds.length === 1
                      ? products.find((p) => p.id === filters.productIds[0])?.name ||
                        filters.productIds[0]
                      : `${filters.productIds.length} selected`}
                    <X
                      className="h-4 w-4 cursor-pointer hover:text-destructive transition-colors"
                      onClick={() => updateFilter("productIds", [])}
                    />
                  </span>
                )}
                {filters.transactionType !== "both" && (
                  <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground">
                    Transaction:{" "}
                    {filters.transactionType === "buy"
                      ? "Buy Orders"
                      : "Sell Orders"}
                    <X
                      className="h-4 w-4 cursor-pointer hover:text-destructive transition-colors"
                      onClick={() => updateFilter("transactionType", "both")}
                    />
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
